import { useEffect, useState } from 'react';
import {
  Link,
  createSearchParams,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useMeQuery } from '../../graphql/types';
import {
  defaultFilterParams,
  getLotFilterParams,
  setLotFilterParams,
} from '../../utils/lot-filter-params';
import { IconButton } from '../button/icon-button';
import { CheckBox } from '../checkbox/checkbox';
import { Container } from '../container/container';
import { FieldGroup } from '../field-group/field-group';
import { SheetHeader } from '../header/sheet-header';
import { Radio } from '../radio/radio';
import { SheetContainer } from '../sheet/sheet-container';
import { SrOnly } from '../sr-only/sr-only';
import CloseSvg from '../svg/close';

export const FilterPanel = () => {
  const [initialized, setInitialized] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { year } = useParams();
  const filterParams = getLotFilterParams(searchParams);

  const me = useMeQuery({
    fetchPolicy: 'cache-only',
    errorPolicy: 'all',
  });

  if (me.data?.activeAuction?.maxFairMarketValue) {
    defaultFilterParams.rangeMax = me.data.activeAuction.maxFairMarketValue;
    if (!initialized) {
      setLotFilterParams(filterParams, { rangeMax: defaultFilterParams });
      setInitialized(true);
    }
  }

  const updateSearchParams = (params: any) => {
    if (
      window.location.search.substring(1) !==
      createSearchParams(params).toString()
    ) {
      setSearchParams(params, { replace: true });
    }
  };

  const setQuery = (value: any, checked?: boolean) => {
    updateSearchParams(setLotFilterParams(filterParams, value, checked));
  };

  useEffect(() => {
    if (!year) {
      return;
    }

    me.refetch({
      year: parseInt(year),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  return (
    <>
      <FieldGroup title="Sort by" divider={false}>
        <Radio
          name="sort"
          label="Lot number - Ascending"
          onChange={() => setQuery({ sort: 'asc' })}
          checked={filterParams.sort === 'asc'}
        />
        <Radio
          name="sort"
          label="Lot number - Descending"
          onChange={() => setQuery({ sort: 'desc' })}
          checked={filterParams.sort === 'desc'}
        />
      </FieldGroup>
      <FieldGroup title="Type">
        <CheckBox
          name="red"
          onChange={(e) => {
            setQuery({ type: 'red' }, e.target.checked);
          }}
          checked={filterParams.type?.includes('red')}
        >
          Red
        </CheckBox>
        <CheckBox
          name="white"
          onChange={(e) => {
            setQuery({ type: 'white' }, e.target.checked);
          }}
          checked={filterParams.type?.includes('white')}
        >
          White
        </CheckBox>
      </FieldGroup>
      <FieldGroup title="Grape">
        {(me.data?.activeAuction?.varieties || []).map((variety) => (
          <CheckBox
            key={variety}
            name={variety}
            onChange={(e) => {
              setQuery({ variety }, e.target.checked);
            }}
            checked={filterParams.variety?.includes(variety)}
          >
            {variety}
          </CheckBox>
        ))}
      </FieldGroup>
    </>
  );
};

export const FilterSheet = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const prevLocation = `${location.pathname.replace('/filters', '')}`;
  const backLocation = `${prevLocation}?${searchParams.toString()}`;
  return (
    <SheetContainer
      initial={{ opacity: 0, y: 32 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: { duration: 0.18 },
      }}
      exit={{
        opacity: 0,
        y: 32,
        transition: { duration: 0.18 },
      }}
    >
      <Container>
        <SheetHeader title="Filters">
          <IconButton as={Link} to={backLocation} variant="tertiary">
            <CloseSvg />
            <SrOnly>Close filters</SrOnly>
          </IconButton>
        </SheetHeader>
        <FilterPanel />
      </Container>
    </SheetContainer>
  );
};
